import React, { useState } from 'react';
import {
  Icon,
  Box,
  Pressable,
  Menu,
  MenuItem,
  VStack,
  Text,
  HStack,
  Badge,
  BadgeText,
} from '@gluestack-ui/themed';
import { RefreshCw, AlertCircle } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useUserJobs, type Job } from '@/common/hooks/useUserJobs';
import { formatRelativeTime } from '@/common/utils/formatRelativeTime';

const MENU_CONTENT_WIDTH = 400;
const WORKING_MODE_APPLY = 'working_mode_apply';

type Translate = (key: string, fallback: string) => string;

const jobDisplayTitle = (job: Job, t: Translate): string => {
  if (job.label) return job.label;
  if (job.type === WORKING_MODE_APPLY) {
    return t('common.jobs.workingModeApply.label', 'Apply student working mode');
  }
  return t(`common.jobs.types.${job.type}`, job.type);
};

type WorkingModeApplyLeaf = {
  taskId: string;
  taskTitle: string;
  ruleId: string;
  status: 'success' | 'failed' | 'skipped';
  attempts: number;
  error?: string;
};

type WorkingModeApplyResult = {
  kind: string;
  moduleId: string;
  version: string;
  leaves: WorkingModeApplyLeaf[];
  summary: { total: number; succeeded: number; failed: number };
};

const parseWorkingModeApplyResult = (result: string | null | undefined): WorkingModeApplyResult | null => {
  if (!result) return null;
  try {
    const parsed = JSON.parse(result) as WorkingModeApplyResult;
    if (parsed?.kind !== WORKING_MODE_APPLY || !Array.isArray(parsed.leaves)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const ruleIdToI18nKey = (ruleId: string): string => {
  const normalized = ruleId.replace(/\./g, '_');
  return `common.jobs.workingModeApply.ruleSteps.${normalized}`;
};

const FailedWorkingModeApplyJob = ({ job }: { job: Job }) => {
  const { t } = useTranslation();
  const parsed = parseWorkingModeApplyResult(job.result);
  const failedLeaves = parsed?.leaves.filter((leaf) => leaf.status === 'failed' || leaf.status === 'skipped') ?? [];
  const succeededCount = parsed?.summary.succeeded ?? 0;

  const summaryText = parsed
    ? t('common.jobs.workingModeApply.summary', {
      failed: parsed.summary.failed,
      total: parsed.summary.total,
      defaultValue: '{{failed}} of {{total}} steps failed',
    })
    : t('common.jobs.failed', 'Failed');

  return (
    <VStack space="xs" w={MENU_CONTENT_WIDTH} py="$1">
      <HStack justifyContent="space-between" alignItems="flex-start" space="sm">
        <Text size="sm" color="$textHeading" fontWeight="medium" numberOfLines={2} flex={1}>
          {jobDisplayTitle(job, t)}
        </Text>
        {!!job.finished && (
          <Text size="xs" color="$textSubtle" flexShrink={0}>
            {formatRelativeTime(job.finished)}
          </Text>
        )}
      </HStack>
      <Badge action="error" variant="solid" size="sm" alignSelf="flex-start">
        <BadgeText>{summaryText}</BadgeText>
      </Badge>
      {failedLeaves.map((leaf) => (
        <HStack
          key={`${leaf.ruleId}:${leaf.taskId}`}
          space="xs"
          alignItems="flex-start"
          testID={`job-tray-failed-leaf-${leaf.taskId}`}
        >
          <Icon as={AlertCircle} size="xs" color="$statusError" mt="$0.5" />
          <VStack flex={1} space="xs">
            <Text size="xs" color="$textHeading" fontWeight="medium" numberOfLines={1}>
              {leaf.taskTitle}
            </Text>
            <Text size="xs" color="$textSubtle">
              {t(ruleIdToI18nKey(leaf.ruleId), leaf.ruleId)}
              {' · '}
              {t('common.jobs.workingModeApply.attempts', {
                count: leaf.attempts,
                defaultValue: '{{count}} attempt',
              })}
            </Text>
            {!!leaf.error && (
              <Text size="xs" color="$statusError" numberOfLines={4}>
                {leaf.error}
              </Text>
            )}
          </VStack>
        </HStack>
      ))}
      {succeededCount > 0 && (
        <Text size="xs" color="$textSubtle" testID="job-tray-success-summary">
          {t('common.jobs.workingModeApply.successSummary', {
            count: succeededCount,
            defaultValue: '{{count}} step completed',
          })}
        </Text>
      )}
      <Text size="xs" color="$textSubtle" mt="$1">
        {t('common.jobs.workingModeApply.recovery', {
          defaultValue: 'Fix the listed tasks (title/description), then Module Settings → Review & apply to resume.',
        })}
      </Text>
    </VStack>
  );
};

const FailedGenericJob = ({ job }: { job: Job }) => {
  const { t } = useTranslation();

  return (
    <VStack space="xs" w={MENU_CONTENT_WIDTH} py="$1">
      <HStack justifyContent="space-between" alignItems="flex-start" space="sm">
        <Text size="sm" color="$textHeading" fontWeight="medium" numberOfLines={2} flex={1}>
          {jobDisplayTitle(job, t)}
        </Text>
        {!!job.finished && (
          <Text size="xs" color="$textSubtle" flexShrink={0}>
            {formatRelativeTime(job.finished)}
          </Text>
        )}
      </HStack>
      <Badge action="error" variant="solid" size="sm" alignSelf="flex-start">
        <BadgeText>{t('common.jobs.failed', 'Failed')}</BadgeText>
      </Badge>
      {!!job.result && (
        <Text size="xs" color="$statusError" numberOfLines={6} testID="job-tray-error-detail">
          {job.result}
        </Text>
      )}
    </VStack>
  );
};

const RunningJobRow = ({ job }: { job: Job }) => {
  const { t } = useTranslation();
  const title = jobDisplayTitle(job, t);
  const leafCount = job.details?.leaves?.length;
  const progressHint = job.type === WORKING_MODE_APPLY && leafCount
    ? t('common.jobs.workingModeApply.progressHint', {
      count: leafCount,
      defaultValue: '{{count}} steps in this apply',
    })
    : null;

  return (
    <VStack space="xs" w={MENU_CONTENT_WIDTH} py="$1">
      <HStack justifyContent="space-between" alignItems="center" space="sm">
        <Text size="sm" color="$textHeading" fontWeight="medium" numberOfLines={2} flex={1}>
          {title}
        </Text>
        <Text size="xs" fontWeight="bold" color="$brandBase">
          {Math.round(job.progress)}
          %
        </Text>
      </HStack>
      {!!job.currentStage && (
        <Text size="xs" color="$textSubtle" numberOfLines={2}>
          {job.currentStage}
        </Text>
      )}
      {!!progressHint && (
        <Text size="xs" color="$textHint" numberOfLines={1}>
          {progressHint}
        </Text>
      )}
      <Box bg="$bgSubtle" h={4} borderRadius="$full" overflow="hidden" mt="$0.5">
        <Box
          bg="$brandBase"
          h="100%"
          borderRadius="$full"
          w={`${Math.max(job.progress, 4)}%`}
        />
      </Box>
    </VStack>
  );
};

/**
 * JobStatusIcon
 * Visible while background jobs are running or recent failures need attention.
 * Shows a spinner or error icon with a count badge; clicking opens a compact
 * dropdown listing each job's progress or failure detail.
 */
const JobStatusIcon = () => {
  const { t } = useTranslation();
  const { data: jobInfo, refetch } = useUserJobs();
  const [isOpen, setIsOpen] = useState(false);

  const runningJobs = jobInfo?.runningJobs || [];
  const recentFailedJobs = jobInfo?.recentFailedJobs || [];
  const trayBadgeCount = jobInfo?.trayBadgeCount ?? 0;
  const hasRecentFailuresOnly = recentFailedJobs.length > 0 && runningJobs.length === 0;

  if (!jobInfo?.hasTrayAttention) return null;

  const iconColor = hasRecentFailuresOnly ? '$statusError' : '$brandBase';
  const triggerBg = hasRecentFailuresOnly ? '$statusErrorSubtle' : '$bgBrandSubtle';
  const badgeBg = hasRecentFailuresOnly ? '$statusError' : '$brandBase';
  const TrayIcon = hasRecentFailuresOnly ? AlertCircle : RefreshCw;

  return (
    <Menu
      placement="bottom right"
      offset={8}
      borderRadius="$xl"
      borderWidth={1}
      borderColor="$borderLight"
      bg="$bgCard"
      py="$1.5"
      softShadow="3"
      onOpen={() => {
        setIsOpen(true);
        refetch();
      }}
      onClose={() => setIsOpen(false)}
      trigger={({ ...triggerProps }) => (
        <Pressable
          {...triggerProps}
          p="$2"
          borderRadius="$lg"
          bg={isOpen ? triggerBg : 'transparent'}
          $hover={{ bg: triggerBg }}
          testID="job-status-icon"
        >
          <Box position="relative">
            <Icon as={TrayIcon} size="sm" color={iconColor} />
            <Box
              position="absolute"
              top={-5}
              right={-5}
              bg={badgeBg}
              borderRadius="$full"
              minWidth={16}
              h={16}
              alignItems="center"
              justifyContent="center"
              px="$1"
              borderWidth={1.5}
              borderColor="$bgCard"
              testID="job-status-badge"
            >
              <Text size="xs" color="$white" fontWeight="bold" lineHeight={14}>
                {trayBadgeCount}
              </Text>
            </Box>
          </Box>
        </Pressable>
      )}
    >
      <MenuItem key="title" textValue="Jobs" disabled closeOnSelect={false} opacity={1} $hover={{ bg: 'transparent' }}>
        <Text size="xs" color="$textSubtle" fontWeight="bold" textTransform="uppercase" letterSpacing={0.6} w={MENU_CONTENT_WIDTH}>
          {t('common.jobs.title', 'Background Jobs')}
        </Text>
      </MenuItem>
      {runningJobs.map((job: Job) => (
        <MenuItem key={job.id} textValue={job.label || job.type} closeOnSelect={false} $hover={{ bg: 'transparent' }}>
          <RunningJobRow job={job} />
        </MenuItem>
      ))}
      {recentFailedJobs.map((job: Job) => (
        <MenuItem key={job.id} textValue={job.label || job.type} closeOnSelect={false} $hover={{ bg: 'transparent' }}>
          {job.type === WORKING_MODE_APPLY ? (
            <FailedWorkingModeApplyJob job={job} />
          ) : (
            <FailedGenericJob job={job} />
          )}
        </MenuItem>
      ))}
    </Menu>
  );
};

export default JobStatusIcon;
